import { apiClient } from '../services/api-client';

/**
 * Fetches a `/food/*` or `/order` endpoint on the foodcourt API.
 *
 * The response is unvalidated JSON, so `TRow[]` is an unchecked claim confined to this
 * boundary rather than validation — see the note in `LyticsFetcher`. Every endpoint
 * behind this fetcher returns an array and only the element type varies, so that
 * element type is caller-supplied, defaulting to `unknown`.
 *
 * Returns `undefined` without calling the API when `endPoint` is empty — call
 * sites rely on this to defer a fetch until a key is ready.
 */
const FoodFetcher = async <TRow = unknown>(
  endPoint: string,
  token: string
): Promise<TRow[] | undefined> => {
  if (!endPoint) {
    return;
  }

  const data = await apiClient.get<TRow[]>(import.meta.env.VITE_FOODCOURT_API_NAME, endPoint, {
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
  });
  return data;
};

/**
 * Fetches several foodcourt endpoints in parallel, one response per URL in order.
 *
 * Call sites pass URLs hitting different endpoints in one call
 * (`/food/bundle-item`, `/food/item`, `/food/price`) and read `dataset[0..3]` as
 * different shapes, so the result is described by a caller-supplied TUPLE rather than
 * one element type — the same unchecked-claim-at-the-boundary arrangement `FoodFetcher`
 * uses, widened to a heterogeneous, variable-length list.
 *
 * The tuple's members should each be optional: a URL whose endpoint is empty resolves
 * `undefined`, and the trailing URLs are only pushed under a runtime `if`.
 *
 * The widening through `unknown` is the assertion TypeScript asks for here, and it is
 * accurate: the runtime value is a positional array of parsed JSON whose length follows
 * the caller's URL list, and nothing at this boundary can check it against `TDataset`.
 * The claim is the caller's, made once, in one place.
 */
// TODO: Remove this
export function MultiFetcher<TDataset extends readonly unknown[] = (unknown[] | undefined)[]>(
  token: string,
  ...urls: string[]
): Promise<TDataset> {
  return Promise.all(urls.map((url) => FoodFetcher(url, token))) as unknown as Promise<TDataset>;
}

export function MultiFoodFetcher(
  token: string,
  ...urls: string[]
): Promise<(unknown[] | undefined)[]> {
  return Promise.all(urls.map((url) => FoodFetcher(url, token)));
}

export default FoodFetcher;
